<template>
  <section class="w-4/6 mx-auto text-secondary transition-colors duration-800 xl:w-11/12 sm:w-full">
    <div class="mb-32" v-colors="{ primary: '#8BE08B', secondary: '#7939FF' }">
      <h1 ref="target" class="w-9/12 lg:w-full sm:text-2xl">
        {{ $t('how.title') }}
        <br />
        <span class="text-orange">{{ $t('how.sub') }}</span>
      </h1>
    </div>
    <div class="mb-64" v-colors="{ primary: '#FBD302', secondary: '#7939FF' }">
      <h2>{{ $t('how.select.title') }}</h2>
      <div class="my-28">
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">1.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.select.search.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.select.search.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">2.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.select.impress.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.select.impress.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">3.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.select.mail.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.select.mail.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">4.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.select.get-in.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.select.get-in.content') }}
          </p>
        </div>
      </div>
      <div class="mt-48 py-24 bg-purple text-yellow rounded-l">
        <div class="mb-4 h-14 overflow-hidden relative">
          <Transition>
            <h2 v-if="trip && trip.duration > 0" class="text-center absolute w-full normal-case">
              <span v-if="trip.duration >= 60">
                {{ Math.floor(trip.duration / 60) }}{{ $t('ui.hour-short') }} {{ trip.duration % 60
                }}{{ $t('ui.minute-short') }}
              </span>
              <span v-else> {{ trip.duration % 60 }} {{ $t('ui.minute-short') }} </span>
              <span class="text-white text-base font-sans normal-case ms-2">
                {{ trip.distance }}{{ $t('ui.distance') }}</span
              >
            </h2>
            <h2 v-else class="text-center absolute w-full">{{ $t('how.select.how-far') }}</h2>
          </Transition>
        </div>
        <div class="flex items-center justify-center gap-6 h-12 lg:flex-col lg:h-auto">
          <div class="flex h-12 gap-2 relative flex-shrink-0 max-w-full sm:px-4">
            <button
              class="h-full rounded-s aspect-square flex border border-white border-opacity-20 hover:bg-white hover:bg-opacity-5 transition-colors"
              @click="getUserLocation"
            >
              <img class="m-auto w-1/2" src="/img/location.svg" alt="" />
            </button>
            <input
              class="text-lg uppercase font-wide px-4 py-3 rounded-s min-w-0 text-white bg-purple border border-white border-opacity-20 placeholder-white placeholder-opacity-50 hover:bg-white hover:bg-opacity-5 transition-colors"
              v-model="address"
              @click="suggestionsVisible = true"
              @blur="if (!hoveringSuggestions) suggestionsVisible = false;"
              :placeholder="
                userLocationUsed ? $t('how.select.your-location') : $t('how.select.start-location')
              "
            />
            <ul
              class="absolute top-full mt-1 suggestions bg-white rounded-s text-black font-sans border-spacing-4 shadow overflow-hidden border border-black-10 w-full w-min-full"
              @mouseenter="hoveringSuggestions = true"
              @mouseleave="hoveringSuggestions = false"
              v-if="suggestions.length && suggestionsVisible"
            >
              <li
                class="border-b border-black-10 px-4 py-2 cursor-pointer hover:bg-black-4"
                v-for="(suggestion, index) in suggestions"
                :key="index"
                @click="selectSuggestion(suggestion)"
              >
                {{ suggestion.place_name }}
              </li>
            </ul>
          </div>
          <img class="h-full lg:hidden" src="/img/arrow_right.svg" alt="" />
          <img class="h-full hidden lg:block" src="/img/arrow_down.svg" alt="" />
          <h3 class="text-white">{{ $t('how.select.destination') }}</h3>
        </div>
      </div>
      <div
        class="flex px-16 py-8 mt-4 bg-purple text-pink rounded-l items-center gap-10 md:flex-col md:px-8"
      >
        <img class="h-24" src="/img/badge_pickup.svg" alt="" />
        <p class="text-xl md:text-lg md:text-center">{{ $t('how.select.collect') }}</p>
      </div>
    </div>
    <div class="my-64" v-colors.dark="{ primary: '#7939FF', secondary: '#FDB9DD' }">
      <h2>{{ $t('how.drive.title') }}</h2>
      <div class="my-28">
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">1.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.drive.maintenance.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.drive.maintenance.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">2.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.drive.no-limit.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.drive.no-limit.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">3.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.drive.international.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.drive.international.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">4.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.drive.coverage.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.drive.coverage.content') }}
          </p>
        </div>
      </div>
    </div>
    <div class="my-64" v-colors="{ primary: '#FDB9DD', secondary: '#F25F12' }">
      <h2>{{ $t('how.return.title') }}</h2>
      <div class="my-28">
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">1.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.return.parking.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.return.parking.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">2.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.return.clean.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.return.clean.content') }}
          </p>
        </div>
        <div class="flex gap-4 my-20 md:flex-col">
          <h3 class="w-1/12 text-3xl md:w-full">3.</h3>
          <h3 class="w-5/12 md:w-full">{{ $t('how.return.deposit.leading') }}</h3>
          <p class="w-5/12 grow text-lg md:w-full">
            {{ $t('how.return.deposit.content') }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
const { locale, t } = useI18n();
const { calculateTrip, getPlacesUrl } = useMapboxHelper();

const { frontendBaseUrl } = useRuntimeConfig().public;
useSeoMeta({
  title: t('seo-meta.how.title'),
  ogTitle: t('seo-meta.how.title'),
  description: t('seo-meta.how.description'),
  ogDescription: t('seo-meta.how.description'),
  ogImage: `${frontendBaseUrl}/img/og/thg-og-how-web.jpg`,
});

// STATE
const suggestionsVisible = ref(false);
const hoveringSuggestions = ref(false);
const address = ref('');
const addressDebounced = refDebounced(address, 750, { maxWait: 2250 });
const userLocationUsed = ref(false);
const tripStart: Ref<[number, number]> = ref([6.552245218518427, 47.363036887844466]);
const trip = ref<null | { distance: number; duration: number }>(null);

const { data } = await useFetch<MapboxGeocoder.Results>(
  () => getPlacesUrl(addressDebounced.value, locale.value),
  {
    immediate: false,
  },
);

const suggestions = ref<MapboxGeocoder.Result[]>([]);
watch(data, () => {
  if (!data.value) {
    suggestions.value = [];
    return;
  }
  suggestions.value = data.value.features as MapboxGeocoder.Result[];
});

// METHODS
const selectSuggestion = async (suggestion: MapboxGeocoder.Result) => {
  const [lng, lat] = suggestion.center;
  tripStart.value = [lng, lat];

  address.value = suggestion.place_name;
  suggestionsVisible.value = false;
  hoveringSuggestions.value = false;
  suggestions.value = [];
  userLocationUsed.value = false;
  trip.value = await calculateTrip({ from: tripStart.value });
};
function getUserLocation() {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        trip.value = await calculateTrip({
          from: [position.coords.longitude, position.coords.latitude],
        });
        userLocationUsed.value = true;
      },
      (error) => {
        console.error('geolocation error:', error);
      },
    );
  } else {
    console.warn('geolocation not supported on this device');
  }
}
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: transform 0.8s cubic-bezier(0.4, 1.5, 0.35, 1);
}
.v-enter-from {
  transform: translateY(100%);
}
.v-leave-to {
  transform: translateY(-100%);
}
</style>
